import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, registerUser } from '../services/api';
import '../styles/AuthModal.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [avatar, setAvatar] = useState<string>('');
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const navigate = useNavigate();

  // 密码强度检查
  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, text: '', color: '' };
    if (password.length < 6) return { strength: 1, text: '弱', color: '#dc2626' };
    if (password.length < 8) return { strength: 2, text: '中等', color: '#f59e0b' };
    return { strength: 3, text: '强', color: '#16a34a' };
  };

  const passwordStrength = getPasswordStrength(password);

  // 键盘快捷键支持
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSubmit(e as any);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('请上传图片文件');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('图片不能超过2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result;
      if (typeof base64 === 'string') {
        setAvatar(base64);
        setAvatarError(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      if (!isLogin && !avatar) {
        setAvatarError('请上传头像');
        setLoading(false);
        return;
      }
      if (isLogin) {
        const res = await loginUser({ username, password });
        localStorage.setItem('token', res.token);
        localStorage.setItem('username', res.username);
        setSuccess('登录成功，正在跳转...');
        setTimeout(() => {
          onClose();
          navigate('/');
        }, 1000);
      } else {
        await registerUser({ username, password, avatar });
        setSuccess('注册成功，请登录');
        setIsLogin(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    setUsername('');
    setPassword('');
    setIsLogin(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="auth-modal-header">
          <h1 className="auth-modal-title">欢迎回来</h1>
          <p className="auth-modal-subtitle">
            {isLogin ? '登录您的账户继续阅读' : '创建新账户开始写作'}
          </p>
        </div>

        <div className="auth-modal-tabs">
          <button
            className={`auth-modal-tab ${isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(true); setError(null); setSuccess(null); }}
          >
            登录
          </button>
          <button
            className={`auth-modal-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(false); setError(null); setSuccess(null); }}
          >
            注册
          </button>
        </div>

        <form onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
          <div className="auth-modal-form-group">
            <input
              type="text"
              className="auth-modal-input"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="用户名"
              required
              autoFocus
            />
          </div>
          {/* 注册时头像上传 */}
          {!isLogin && (
            <div className="auth-modal-form-group">
              <label style={{ fontWeight: 500, marginBottom: 4 }}>头像（必填）</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                required
              />
              {avatar && (
                <img src={avatar} alt="头像预览" style={{ width: 64, height: 64, borderRadius: '50%', marginTop: 8 }} />
              )}
              {avatarError && <div className="auth-modal-message error">{avatarError}</div>}
            </div>
          )}
          <div className="auth-modal-form-group">
            <div className="auth-modal-password-container">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-modal-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="密码"
                required
              />
              <button
                type="button"
                className="auth-modal-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
                    <path d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" fill="currentColor" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M13.586 6.586L12.172 5.172 10 7.344 7.828 5.172 6.414 6.586 8.586 8.758 6.414 10.172 7.828 11.586 10 9.414l2.172 2.172 1.414-1.414L11.414 8.758l2.172-2.172z" fill="currentColor" />
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
                  </svg>
                )}
              </button>
            </div>
            {!isLogin && password && (
              <div className="auth-modal-password-strength">
                <div className="auth-modal-strength-bars">
                  <div
                    className={`auth-modal-strength-bar ${passwordStrength.strength >= 1 ? 'active' : ''}`}
                    style={{ backgroundColor: passwordStrength.strength >= 1 ? passwordStrength.color : '#e0e0e0' }}
                  ></div>
                  <div
                    className={`auth-modal-strength-bar ${passwordStrength.strength >= 2 ? 'active' : ''}`}
                    style={{ backgroundColor: passwordStrength.strength >= 2 ? passwordStrength.color : '#e0e0e0' }}
                  ></div>
                  <div
                    className={`auth-modal-strength-bar ${passwordStrength.strength >= 3 ? 'active' : ''}`}
                    style={{ backgroundColor: passwordStrength.strength >= 3 ? passwordStrength.color : '#e0e0e0' }}
                  ></div>
                </div>
                <span className="auth-modal-strength-text" style={{ color: passwordStrength.color }}>
                  {passwordStrength.text}
                </span>
              </div>
            )}
          </div>
          <button
            type="submit"
            className="auth-modal-submit"
            disabled={loading}
          >
            {loading && <span className="auth-modal-loading-spinner"></span>}
            {isLogin ? (loading ? '登录中...' : '登录') : (loading ? '注册中...' : '注册')}
          </button>
        </form>

        {error && (
          <div className="auth-modal-message error">
            {error}
          </div>
        )}
        {success && (
          <div className="auth-modal-message success">
            {success}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal; 